import { useCurrentUser } from "@app/providers/UserContext";
import { useAuth } from "@laborelec/lbe-react/auth";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Comment } from "@shared/types/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { addComment, deleteComment, getComments, updateComment } from "../api";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";

interface Props {
  powerPlantId: string;
  systemId: string;
  pidId: string;
  open: boolean;
  onClose: () => void;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function CommentsDrawer({
  powerPlantId,
  systemId,
  pidId,
  open,
  onClose,
}: Props) {
  const { isUser } = useAuth();
  const isSuperUser = isUser("super_user");
  const { userId: currentUserId } = useCurrentUser();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [newText, setNewText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ["comments", powerPlantId, systemId, pidId],
    queryFn: () => getComments(powerPlantId, systemId, pidId),
    enabled: open,
  });

  const addMut = useMutation({
    mutationFn: (text: string) =>
      addComment(powerPlantId, systemId, pidId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["comments", powerPlantId, systemId, pidId],
      });
      queryClient.invalidateQueries({
        queryKey: ["pids", powerPlantId, systemId],
      });
      setNewText("");
      setTimeout(
        () =>
          scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: "smooth",
          }),
        100,
      );
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      updateComment(powerPlantId, systemId, pidId, commentId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["comments", powerPlantId, systemId, pidId],
      });
      setEditingId(null);
      setEditText("");
    },
  });

  const deleteMut = useMutation({
    mutationFn: (commentId: string) =>
      deleteComment(powerPlantId, systemId, pidId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["comments", powerPlantId, systemId, pidId],
      });
      queryClient.invalidateQueries({
        queryKey: ["pids", powerPlantId, systemId],
      });
      setDeleteId(null);
    },
  });

  const canEditOrDelete = (comment: Comment) =>
    comment.user_id === currentUserId || isSuperUser;

  if (!open) return null;

  return (
    <Box
      sx={{
        width: 320,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid #DEE3EA",
        bgcolor: "#FAFBFC",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: "#5C6B7A" }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: "#002A5C", flex: 1 }}
        >
          Comments ({comments.length})
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "#8E99A8" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      {/* Comment list */}
      <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1 }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!isLoading && comments.length === 0 && (
          <Typography
            variant="body2"
            sx={{ color: "#8E99A8", textAlign: "center", py: 4 }}
          >
            No comments yet
          </Typography>
        )}
        {comments.map((comment) => (
          <Box
            key={comment.id}
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: comment.user_id === currentUserId ? "#E3F2FD" : "#fff",
              border: "1px solid",
              borderColor:
                comment.user_id === currentUserId ? "#BBDEFB" : "#ECEFF3",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "#002A5C",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {comment.author_name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#8E99A8", fontSize: "0.65rem", flexShrink: 0 }}
              >
                {formatRelativeTime(comment.created_at)}
              </Typography>
              {canEditOrDelete(comment) && editingId !== comment.id && (
                <>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingId(comment.id);
                        setEditText(comment.text);
                      }}
                      sx={{
                        p: 0.25,
                        color: "#8E99A8",
                        "&:hover": { color: "#009DE0" },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteId(comment.id)}
                      sx={{
                        p: 0.25,
                        color: "#8E99A8",
                        "&:hover": { color: "#E30613" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
            {editingId === comment.id ? (
              <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  maxRows={4}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && editText.trim()) {
                      e.preventDefault();
                      updateMut.mutate({
                        commentId: comment.id,
                        text: editText.trim(),
                      });
                    }
                    if (e.key === "Escape") {
                      setEditingId(null);
                      setEditText("");
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.8rem" } }}
                />
                <IconButton
                  size="small"
                  disabled={!editText.trim() || updateMut.isPending}
                  onClick={() =>
                    updateMut.mutate({
                      commentId: comment.id,
                      text: editText.trim(),
                    })
                  }
                  sx={{ color: "#009DE0", alignSelf: "flex-end" }}
                >
                  <SendIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {comment.text}
              </Typography>
            )}
            {comment.updated_at !== comment.created_at &&
              editingId !== comment.id && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#B0BAC5",
                    fontSize: "0.6rem",
                    mt: 0.25,
                    display: "block",
                  }}
                >
                  edited
                </Typography>
              )}
          </Box>
        ))}
      </Box>

      {/* Input */}
      <Divider />
      <Box sx={{ p: 1.5, display: "flex", gap: 0.5, flexShrink: 0 }}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={3}
          placeholder="Write a comment..."
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && newText.trim()) {
              e.preventDefault();
              addMut.mutate(newText.trim());
            }
          }}
          sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.8rem" } }}
        />
        <IconButton
          size="small"
          disabled={!newText.trim() || addMut.isPending}
          onClick={() => addMut.mutate(newText.trim())}
          sx={{ color: "#009DE0", alignSelf: "flex-end" }}
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <ConfirmDeleteDialog
        open={deleteId !== null}
        title="Delete comment?"
        message="This comment will be permanently removed."
        onClose={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) deleteMut.mutate(deleteId);
        }}
      />
    </Box>
  );
}
